import { useMemo } from 'react';

/**
 * Custom hook for managing filtered data computations in AdminDashboard
 * 
 * @param {Array} users - Array of user objects
 * @param {Array} organizations - Array of organization objects
 * @param {string} userSearch - Search term for users
 * @param {string} organizationSearch - Search term for organizations
 * @param {string} leftSearch - Search term for left pane
 * @param {string} relViewMode - Current view mode ('user' or 'organization')
 * @param {string|null} selectedId - Currently selected entity ID
 * @param {string} searchAssign - Search term for assignment
 * @param {string} ownerSearch - Search term for owner autocomplete
 * @param {string} assignRootAdminSearch - Search term for assign root admin autocomplete
 * @param {number} usersPage - Current users page
 * @param {number} organizationsPage - Current organizations page
 * @param {number} ITEMS_PER_PAGE - Items per page constant
 * @returns {Object} Object containing all filtered and paginated data
 */
export const useFilteredData = ({
    users,
    organizations,
    userSearch,
    organizationSearch,
    leftSearch,
    relViewMode,
    selectedId,
    searchAssign,
    ownerSearch,
    assignRootAdminSearch,
    usersPage,
    organizationsPage,
    ITEMS_PER_PAGE,
    pendingUserIds = []
}) => {
    const pendingIds = useMemo(() => new Set(pendingUserIds), [pendingUserIds]);

    // Directory Filters
    const filteredUsers = useMemo(() =>
        users.filter(u => !pendingIds.has(u.id)).filter(u => 
            (u.email || '').toLowerCase().includes(userSearch.toLowerCase()) ||
            (u.fullName || '').toLowerCase().includes(userSearch.toLowerCase()) ||
            (u.company || '').toLowerCase().includes(userSearch.toLowerCase())
        ),
        [users, userSearch, pendingIds]
    );

    const filteredOrganizations = useMemo(() =>
        organizations.filter(t => 
            (t.id || '').toLowerCase().includes(organizationSearch.toLowerCase()) ||
            (t.name || '').toLowerCase().includes(organizationSearch.toLowerCase())
        ),
        [organizations, organizationSearch]
    );

    // Split Pane Filters
    const filteredLeftUsers = useMemo(() =>
        users.filter(u => 
            (u.email || '').toLowerCase().includes(leftSearch.toLowerCase()) ||
            (u.fullName || '').toLowerCase().includes(leftSearch.toLowerCase())
        ),
        [users, leftSearch]
    );

    const filteredLeftOrganizations = useMemo(() =>
        organizations.filter(t => 
            (t.id || '').toLowerCase().includes(leftSearch.toLowerCase()) ||
            (t.name || '').toLowerCase().includes(leftSearch.toLowerCase())
        ),
        [organizations, leftSearch]
    );

    // Top-100 Limiters for Relationships left pane
    const displayedLeftUsers = useMemo(() =>
        filteredLeftUsers.slice(0, 100),
        [filteredLeftUsers]
    );

    const displayedLeftOrganizations = useMemo(() =>
        filteredLeftOrganizations.slice(0, 100),
        [filteredLeftOrganizations]
    );

    // Pagination computations for directories
    const paginatedUsers = useMemo(() =>
        filteredUsers.slice((usersPage - 1) * ITEMS_PER_PAGE, usersPage * ITEMS_PER_PAGE),
        [filteredUsers, usersPage, ITEMS_PER_PAGE]
    );

    const paginatedOrganizations = useMemo(() =>
        filteredOrganizations.slice((organizationsPage - 1) * ITEMS_PER_PAGE, organizationsPage * ITEMS_PER_PAGE),
        [filteredOrganizations, organizationsPage, ITEMS_PER_PAGE]
    );

    const totalUsersPages = useMemo(() =>
        Math.ceil(filteredUsers.length / ITEMS_PER_PAGE) || 1,
        [filteredUsers.length, ITEMS_PER_PAGE]
    );

    const totalOrganizationsPages = useMemo(() =>
        Math.ceil(filteredOrganizations.length / ITEMS_PER_PAGE) || 1,
        [filteredOrganizations.length, ITEMS_PER_PAGE]
    );

    // Autocomplete filter for Root Admin Owner in modal
    const filteredOwnerOptions = useMemo(() =>
        users.filter(u => 
            (u.email || '').toLowerCase().includes(ownerSearch.toLowerCase()) ||
            (u.fullName || '').toLowerCase().includes(ownerSearch.toLowerCase())
        ),
        [users, ownerSearch]
    );

    // Autocomplete filter for Assign Root Admin Modal
    const filteredAssignRootAdminOptions = useMemo(() =>
        users.filter(u => 
            (u.email || '').toLowerCase().includes(assignRootAdminSearch.toLowerCase()) ||
            (u.fullName || '').toLowerCase().includes(assignRootAdminSearch.toLowerCase())
        ),
        [users, assignRootAdminSearch]
    );

    // Selected Entity details
    const selectedUser = useMemo(() =>
        relViewMode === 'user' ? users.find(u => u.id === selectedId) : null,
        [relViewMode, users, selectedId]
    );

    const selectedOrganization = useMemo(() =>
        relViewMode === 'organization' ? organizations.find(t => t.id === selectedId) : null,
        [relViewMode, organizations, selectedId]
    );

    const selectedEntityName = useMemo(() =>
        relViewMode === 'user' 
            ? (selectedUser ? (selectedUser.fullName || selectedUser.email) : '')
            : (selectedOrganization ? selectedOrganization.name : ''),
        [relViewMode, selectedUser, selectedOrganization]
    );

    // Mapped items (assigned organizations or users)
    const mappedItems = useMemo(() =>
        relViewMode === 'user'
            ? (selectedUser ? selectedUser.organizations : [])
            : (selectedOrganization ? users.filter(u => u.organizations.some(ut => ut.id === selectedId)).map(u => ({ id: u.id, name: u.fullName || u.email })) : []),
        [relViewMode, selectedUser, selectedOrganization, users, selectedId]
    );

    // Filtered options available to assign (excluding already assigned)
    const filteredAssignOptions = useMemo(() =>
        relViewMode === 'user'
            ? organizations
                .filter(t => !mappedItems.some(item => item.id === t.id))
                .filter(t => 
                    t.id.toLowerCase().includes(searchAssign.toLowerCase()) || 
                    t.name.toLowerCase().includes(searchAssign.toLowerCase())
                )
                .map(t => ({ id: t.id, name: t.name }))
            : users
                .filter(u => !u.organizations.some(ut => ut.id === selectedId))
                .filter(u => 
                    u.email.toLowerCase().includes(searchAssign.toLowerCase()) || 
                    (u.fullName || '').toLowerCase().includes(searchAssign.toLowerCase())
                )
                .map(u => ({ id: u.id, name: u.fullName || u.email })),
        [relViewMode, organizations, users, mappedItems, searchAssign, selectedId]
    );

    return {
        filteredUsers,
        filteredOrganizations,
        filteredLeftUsers,
        filteredLeftOrganizations,
        displayedLeftUsers,
        displayedLeftOrganizations,
        paginatedUsers,
        paginatedOrganizations,
        totalUsersPages,
        totalOrganizationsPages,
        filteredOwnerOptions,
        filteredAssignRootAdminOptions,
        selectedUser,
        selectedOrganization,
        selectedEntityName,
        mappedItems,
        filteredAssignOptions
    };
};
